// Shared drag handle for react-resizable-panels (DCA analyzer + Type Curve).
//
// One component, both orientations. Wraps <PanelResizeHandle> and renders a
// thin, dark-theme bar (~6px) with a hover highlight, an active (dragging)
// highlight, the correct col-/row-resize cursor, and a small centred grip so
// the divider reads as "grabbable" rather than dead space. Styles are inlined
// (no SCSS build-order dependency) so the two pages can share it verbatim.
import { useState } from "react";
import { PanelResizeHandle } from "react-resizable-panels";

const BASE = "#2a2f4a"; // matches the app's card chrome (#262b49) family
const HOVER = "#3a63c0"; // brighter accent on hover
const ACTIVE = "#0d6efd"; // full accent while dragging

// Grip dots so the thin bar reads as an interactive divider.
function Grip({ direction, color }) {
  const dotStyle = {
    background: color,
    borderRadius: "50%",
    width: 3,
    height: 3,
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: direction === "horizontal" ? "column" : "row",
        gap: 3,
        alignItems: "center",
        justifyContent: "center",
        opacity: 0.85,
      }}
    >
      <span style={dotStyle} />
      <span style={dotStyle} />
      <span style={dotStyle} />
    </div>
  );
}

/**
 * @param {"horizontal"|"vertical"} direction  Orientation of the PARENT
 *        PanelGroup. "horizontal" → panels side-by-side → a VERTICAL bar with a
 *        col-resize cursor. "vertical" → panels stacked → a HORIZONTAL bar with
 *        a row-resize cursor.
 */
function ResizeHandle({ direction = "horizontal", id }) {
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);

  const isCol = direction === "horizontal"; // side-by-side → vertical grabber
  const thickness = 6;
  const color = dragging ? ACTIVE : hovered ? HOVER : BASE;
  const gripColor = dragging || hovered ? "#dfe3ff" : "rgba(223,227,255,0.55)";

  return (
    <PanelResizeHandle
      id={id}
      onDragging={(active) => setDragging(active)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        background: color,
        transition: "background 0.15s ease",
        cursor: isCol ? "col-resize" : "row-resize",
        // A vertical grabber is thin-wide + full-tall; horizontal is the reverse.
        width: isCol ? thickness : "100%",
        height: isCol ? "100%" : thickness,
        // Keep the visible bar itself thin but give a small margin so adjacent
        // panels' rounded corners don't touch it.
        margin: isCol ? "0 2px" : "2px 0",
        borderRadius: 3,
        zIndex: 3,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Grip direction={direction} color={gripColor} />
    </PanelResizeHandle>
  );
}

export default ResizeHandle;
